import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Typography, Card, message, Alert, Checkbox, Tooltip } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, InfoCircleOutlined, SendOutlined, EyeInvisibleOutlined, EyeTwoTone, QuestionCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import LandingLayout from '../components/Layout/LandingLayout';

const { Title, Paragraph } = Typography;

interface RegisterFormData {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirmPassword: string;
  privacyPolicy: boolean;
  termsOfService: boolean;
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const getPasswordTooltip = () => (
    <div style={{ maxWidth: '250px' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Требования к паролю:</div>
      <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
        • Минимум 8 символов<br/>
        • Минимум 3 из 4 критериев:<br/>
        &nbsp;&nbsp;- Заглавные буквы (A-Z)<br/>
        &nbsp;&nbsp;- Строчные буквы (a-z)<br/>
        &nbsp;&nbsp;- Цифры (0-9)<br/>
        &nbsp;&nbsp;- Спецсимволы (!@#$%^&*)
      </div>
    </div>
  );

  const onFinish = async (values: RegisterFormData) => {
    setLoading(true);
    try {
      await register({
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password,
        privacy_policy_accepted: values.privacyPolicy,
        terms_of_service_accepted: values.termsOfService,
      });
      message.success(t('register.success'));
      navigate('/login');
    } catch (error) {
      message.error(t('register.error'));
    } finally {
      setLoading(false);
    }
  };

  const formCardStyle = {
    width: '100%',
    padding: '2px',
    background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(26, 86, 219, 0.15)',
    border: 'none',
    overflow: 'hidden'
  };

  const innerFormStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '40px 20px',
    position: 'relative' as const,
    overflow: 'hidden'
  };

  const fieldStyle = (fieldName: string) => ({
    height: '52px',
    fontSize: '16px',
    borderRadius: '12px',
    border: focusedField === fieldName ? '2px solid #1a56db' : '2px solid #e8e8e8',
    boxShadow: focusedField === fieldName ? '0 0 0 3px rgba(26, 86, 219, 0.1)' : 'none',
    transition: 'all 0.3s ease',
    background: focusedField === fieldName ? '#f6f9ff' : 'white'
  });

  const buttonStyle = {
    height: '52px',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
    border: 'none',
    boxShadow: '0 8px 16px rgba(26, 86, 219, 0.3)',
    transition: 'all 0.3s ease',
    position: 'relative' as const,
    overflow: 'hidden'
  };

  return (
    <LandingLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '24px', 
          width: '100%' 
        }}>
          {/* Access Information Card */}
          <Card style={{ width: '100%' }}>
            <Alert
              message={<Title level={3} style={{ margin: 0, color: '#1890ff' }}>{t('register.accessInfo.title')}</Title>}
              description={
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '16px', 
                  width: '100%' 
                }}>
                  <Paragraph style={{ margin: 0, fontSize: '16px', lineHeight: '1.6' }}>
                    {t('register.accessInfo.description')}
                  </Paragraph>
                  <Paragraph style={{ margin: 0, fontSize: '16px', lineHeight: '1.6' }}>
                    {t('register.accessInfo.process')}
                  </Paragraph>
                  <Paragraph style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                    {t('register.accessInfo.telegram')}
                  </Paragraph>
                  <Paragraph style={{ margin: '12px 0 0 0', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    {t('register.accessInfo.note')}
                  </Paragraph>
                  <div style={{ marginTop: '16px' }}>
                    <a 
                      href="https://t.me/QAappbot" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Button 
                        type="primary" 
                        icon={<SendOutlined />} 
                        size="large"
                        style={{ 
                          background: '#1890ff',
                          borderColor: '#1890ff',
                          fontWeight: 'bold'
                        }}
                      >
                        @QAappbot
                      </Button>
                    </a>
                  </div>
                </div>
              }
              type="info"
              icon={<InfoCircleOutlined style={{ fontSize: '24px' }} />}
              showIcon
              style={{
                padding: '24px',
                border: '2px solid #1890ff',
                borderRadius: '12px',
                background: '#f6f9ff'
              }}
            />
          </Card>

          {/* Registration Form Card */}
          <Card style={formCardStyle}>
            <div style={innerFormStyle}>
              {/* Декоративные элементы */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '100px',
                height: '100px',
                background: 'linear-gradient(135deg, rgba(26, 86, 219, 0.1) 0%, rgba(24, 144, 255, 0.1) 100%)',
                borderRadius: '50%',
                zIndex: 0
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, rgba(26, 86, 219, 0.05) 0%, rgba(24, 144, 255, 0.05) 100%)',
                borderRadius: '50%',
                zIndex: 0
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <Title level={2} style={{ 
                  textAlign: 'center', 
                  marginBottom: 40, 
                  fontSize: '32px',
                  background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold'
                }}>
                  {t('register.title')}
                </Title>
                
                <div style={{ width: '100%', margin: '0 auto' }}>
                  <Form
                    name="register"
                    onFinish={onFinish}
                    layout="vertical"
                    size="large"
                    style={{ width: '100%' }}
                  >
                    <Form.Item
                      name="email"
                      rules={[
                        { required: true, message: t('register.emailRequired') },
                        { type: 'email', message: t('register.emailInvalid') }
                      ]}
                      style={{ marginBottom: '24px' }}
                    >
                      <Input
                        prefix={<MailOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                        placeholder={t('register.email')}
                        size="large"
                        style={fieldStyle('email')}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                      />
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                      <Form.Item
                        name="first_name"
                        rules={[{ required: true, message: t('register.firstNameRequired') }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          prefix={<UserOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                          placeholder={t('register.firstName')}
                          size="large"
                          style={fieldStyle('first_name')}
                          onFocus={() => setFocusedField('first_name')}
                          onBlur={() => setFocusedField(null)}
                        />
                      </Form.Item>

                      <Form.Item
                        name="last_name"
                        rules={[{ required: true, message: t('register.lastNameRequired') }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          prefix={<UserOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                          placeholder={t('register.lastName')}
                          size="large"
                          style={fieldStyle('last_name')}
                          onFocus={() => setFocusedField('last_name')}
                          onBlur={() => setFocusedField(null)}
                        />
                      </Form.Item>
                    </div>

                    <Form.Item
                      name="password"
                      label={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '16px', fontWeight: '500' }}>
                            {t('register.password')}
                          </span>
                          <Tooltip title={getPasswordTooltip()} placement="topRight">
                            <QuestionCircleOutlined 
                              style={{ 
                                color: '#1a56db', 
                                cursor: 'help',
                                fontSize: '16px'
                              }} 
                            />
                          </Tooltip>
                        </div>
                      }
                      rules={[
                        { required: true, message: t('register.passwordRequired') },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            if (value.length < 8) {
                              return Promise.reject(new Error(t('register.passwordMinLengthError')));
                            }
                            
                            const hasUpperCase = /[A-Z]/.test(value);
                            const hasLowerCase = /[a-z]/.test(value);
                            const hasNumbers = /\d/.test(value);
                            const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
                            
                            const criteriaCount = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
                            if (criteriaCount < 3) {
                              return Promise.reject(new Error(t('register.passwordComplexityError')));
                            }
                            
                            return Promise.resolve();
                          }
                        }
                      ]}
                      style={{ marginBottom: '24px' }}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                        placeholder={t('register.password')}
                        size="large"
                        style={fieldStyle('password')}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#1a56db" /> : <EyeInvisibleOutlined style={{ color: '#1a56db' }} />)}
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      dependencies={['password']}
                      rules={[
                        { required: true, message: t('register.confirmPasswordRequired') },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t('register.passwordsDoNotMatch')));
                          },
                        }),
                      ]}
                      style={{ marginBottom: '32px' }}
                    >
                      <Input.Password
                        prefix={<LockOutlined style={{ fontSize: '18px', color: '#1a56db' }} />}
                        placeholder={t('register.confirmPassword')}
                        size="large"
                        style={fieldStyle('confirmPassword')}
                        onFocus={() => setFocusedField('confirmPassword')}
                        onBlur={() => setFocusedField(null)}
                        iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#1a56db" /> : <EyeInvisibleOutlined style={{ color: '#1a56db' }} />)}
                      />
                    </Form.Item>

                    {/* Privacy Policy and Terms Checkboxes */}
                    <div style={{ 
                      background: '#f6f9ff',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '1px solid #e8e8e8',
                      marginBottom: '24px'
                    }}>
                      <Form.Item
                        name="privacyPolicy"
                        valuePropName="checked"
                        rules={[
                          { required: true, message: t('register.privacyPolicyRequired') }
                        ]}
                        style={{ marginBottom: '16px' }}
                      >
                        <Checkbox style={{ fontSize: '14px', lineHeight: '1.6' }}>
                          {t('register.acceptPrivacyPolicy')}{' '}
                          <Link 
                            to="/privacy" 
                            target="_blank"
                            style={{ 
                              color: '#1a56db',
                              fontWeight: 'bold',
                              textDecoration: 'underline'
                            }}
                          >
                            {t('register.privacyPolicyLink')}
                          </Link>
                        </Checkbox>
                      </Form.Item>

                      <Form.Item
                        name="termsOfService"
                        valuePropName="checked"
                        rules={[
                          { required: true, message: t('register.termsRequired') }
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Checkbox style={{ fontSize: '14px', lineHeight: '1.6' }}>
                          {t('register.acceptTerms')}{' '}
                          <Link 
                            to="/terms" 
                            target="_blank"
                            style={{ 
                              color: '#1a56db',
                              fontWeight: 'bold',
                              textDecoration: 'underline'
                            }}
                          >
                            {t('register.termsLink')}
                          </Link>
                        </Checkbox>
                      </Form.Item>
                    </div>

                    <Form.Item style={{ marginBottom: '24px' }}>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        block 
                        size="large"
                        loading={loading}
                        style={buttonStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 12px 24px rgba(26, 86, 219, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(26, 86, 219, 0.3)';
                        }}
                      >
                        {loading ? t('register.submitting') : t('register.submit')}
                      </Button>
                    </Form.Item>

                    <div style={{ 
                      textAlign: 'center', 
                      fontSize: '16px',
                      padding: '16px 0',
                      borderTop: '1px solid #e8e8e8',
                      marginTop: '16px'
                    }}>
                      <span style={{ color: '#666' }}>{t('register.haveAccount')} </span>
                      <Link 
                        to="/login"
                        style={{ 
                          fontSize: '16px',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #1a56db 0%, #1890ff 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          textDecoration: 'none'
                        }}
                      >
                        {t('register.login')}
                      </Link>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </LandingLayout>
  );
};

export default RegisterPage; 